import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { Container } from "../layout/Container.jsx";
import { fetchFacades } from "../../api/public.api.js";
import { assetUrl, variantUrl } from "../../api/client.js";
import { ImageWithSkeleton } from "../shared/ImageWithSkeleton.jsx";

/**
 * Homepage facades mosaic. Pulls EVERY published facade (no `isFeatured`
 * filter) and randomly picks up to six per page-load to fill the
 * mosaic — same dynamic pattern the Interior gallery uses. Title
 * overlays are intentionally omitted so the imagery speaks for itself.
 *
 * Grid tiles request the 800px WebP variant (`-md.webp`) — the seed
 * facade originals are 15-20 MB each, the variants ~70-100 KB.
 *
 * The section is always rendered. While the query is loading, a six-tile
 * shimmer skeleton fills the grid. If the API truly returns nothing, an
 * empty-state replaces the grid so the visitor still sees the heading.
 */
const TILE_LAYOUT = ["row-span-2", "", "", "row-span-2", "", ""];
const HOME_TILES = 6;

export function FacadesShowcase() {
  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ["public", "facades", "home-showcase-all"],
    queryFn: fetchFacades,
    staleTime: 5 * 60_000,
  });

  // Random pick locked in once per fetch cycle so tiles don't reshuffle
  // on every render — only when React Query refreshes the data.
  const tiles = useMemo(() => {
    const pool = (data ?? []).filter((f) => f.image);
    if (pool.length === 0) return [];
    return shuffle(pool)
      .slice(0, HOME_TILES)
      .map((f) => ({
        // Try the 800px WebP variant first; fall back to the original
        // when the variant hasn't been generated on the server (e.g.
        // fresh deploy where optimize-images hasn't been run yet).
        src: variantUrl(f.image, "md"),
        fallbackSrc: assetUrl(f.image),
        alt: f.title,
        key: f._id ?? f.slug ?? f.title,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataUpdatedAt]);

  return (
    <section className="border-t border-border-subtle bg-bg-elevated py-20 lg:py-28">
      <Container>
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="eyebrow">Inspiration</p>
            <h2 className="mt-3 font-display text-4xl text-text-primary lg:text-5xl">
              Recent work, real homes
            </h2>
            <p className="mt-4 text-text-secondary">
              A glimpse of the facade variety we build — from classic-brick family
              homes to contemporary double-storey designs.
            </p>
          </div>
          <Link
            to="/facades"
            className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover"
          >
            Browse all facades
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid auto-rows-[160px] grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:auto-rows-[200px]">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`animate-pulse rounded-2xl bg-bg-elevated-2 ${TILE_LAYOUT[i] ?? ""}`}
              />
            ))}
          </div>
        ) : tiles.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated-2 px-6 py-16 text-center text-text-secondary">
            Facade photography coming soon.
          </p>
        ) : (
          <div className="grid auto-rows-[160px] grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:auto-rows-[200px]">
            {tiles.map((t, i) => (
              <Link
                key={t.key}
                to="/facades"
                className={`group relative overflow-hidden rounded-2xl bg-bg-elevated-2 ${TILE_LAYOUT[i] ?? ""}`}
              >
                <ImageWithSkeleton
                  src={t.src}
                  fallbackSrc={t.fallbackSrc}
                  alt={t.alt ?? ""}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* No title overlay — soft hover-only fade for visual weight. */}
                <div className="absolute inset-0 bg-gradient-to-t from-bg-base/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
}

/** Fisher–Yates shuffle. Returns a new array; never mutates input. */
function shuffle(arr) {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
